import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../utils/seo';

const TermsOfService: React.FC = () => {
  useSEO({
    title: "Terms of Service - Alertaio",
    description: "Read Alertaio's terms of service to understand the rules and regulations for using our AI API cost monitoring and alert platform.",
    keywords: "terms of service, terms and conditions, user agreement, service terms",
    canonicalUrl: "https://alertaio.com/terms",
    type: "website"
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">Terms of Service</h1>
          <p className="text-slate-600 mb-8">Effective Date: December 22, 2025</p>

          <div className="prose prose-slate max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                Welcome to Alertaio. These Terms of Service ("Terms") govern your access to and use of our website, platform, and services (collectively, the "Service"). By accessing or using our Service, you agree to be bound by these Terms.
              </p>
              <p className="text-slate-700 leading-relaxed">
                If you do not agree to these Terms, you may not access or use the Service. We reserve the right to modify these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
              </p>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Service Description</h2>
              <p className="text-slate-700 leading-relaxed mb-3">
                Alertaio is a monitoring and alerting service that helps users track their AI API costs in real-time. Our Service:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Monitors your AI API usage and costs from providers like OpenAI, Anthropic, and others</li>
                <li>Sends real-time alerts when spending thresholds are reached</li>
                <li>Provides usage analytics and spending insights</li>
                <li>Supports multiple notification channels (Email, Slack, SMS)</li>
                <li>Allows customizable budget limits and alert configurations</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                We use read-only API keys to access your usage data. We do not access, view, or store your AI prompts, completions, or any content processed through AI APIs.
              </p>
            </section>

            {/* Account Registration */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Account Registration and Eligibility</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">3.1 Eligibility</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You must be at least 18 years old and have the legal capacity to enter into contracts to use our Service. By creating an account, you represent and warrant that you meet these requirements.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">3.2 Account Security</h3>
              <p className="text-slate-700 leading-relaxed mb-3">You are responsible for:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized access or security breach</li>
                <li>Ensuring that your account information is accurate and up-to-date</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3 mt-4">3.3 Account Suspension</h3>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to suspend or terminate your account if we suspect fraudulent activity, violation of these Terms, or any activity that may harm the Service or other users.
              </p>
            </section>

            {/* API Keys and Permissions */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. API Keys and Permissions</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">4.1 API Key Requirements</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                To use our Service, you must provide read-only API keys from your AI service providers. You agree that:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li>You have the legal right to provide these API keys</li>
                <li>The API keys have only read-only permissions for usage and billing data</li>
                <li>You are responsible for managing and revoking API keys through your provider's dashboard</li>
                <li>We will use these keys solely to monitor your usage and costs</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">4.2 Data Access Limitations</h3>
              <p className="text-slate-700 leading-relaxed">
                We will only access usage metrics, cost data, and billing information through the provided API keys. We will never access, store, or process your AI prompts, completions, training data, or any content processed through AI APIs.
              </p>
            </section>

            {/* Pricing and Payment */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Pricing and Payment Terms</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.1 Subscription Plans</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Alertaio offers subscription-based pricing at $9 per month. Early access users who sign up during the beta period will lock in this price permanently, even if pricing changes in the future.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.2 Free Trial</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We offer a 14-day free trial for new users. You will not be charged during the trial period. If you do not cancel before the trial ends, your subscription will automatically begin, and you will be charged the monthly fee.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.3 Billing and Automatic Renewal</h3>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li>Subscriptions automatically renew each month on your billing date</li>
                <li>You will be charged the subscription fee unless you cancel before the renewal date</li>
                <li>All fees are non-refundable except as required by law or as stated in our refund policy</li>
                <li>We reserve the right to change pricing with 30 days' notice to active subscribers</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">5.4 Cancellation and Refunds</h3>
              <p className="text-slate-700 leading-relaxed mb-3">
                You may cancel your subscription at any time through your account settings. Cancellations are effective at the end of the current billing period. We offer refunds under the following conditions:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Full refund within 7 days of initial subscription (excluding free trial period)</li>
                <li>Pro-rated refund for service outages exceeding 24 consecutive hours</li>
                <li>No refunds for partial month usage or early cancellation</li>
              </ul>
            </section>

            {/* Acceptable Use Policy */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Acceptable Use Policy</h2>
              <p className="text-slate-700 leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Use the Service for any illegal purpose or in violation of any laws</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Reverse engineer, decompile, or attempt to extract source code from our Service</li>
                <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
                <li>Interfere with or disrupt the Service or servers/networks connected to the Service</li>
                <li>Resell, redistribute, or make the Service available to third parties</li>
                <li>Use the Service to monitor competitors or for competitive intelligence purposes</li>
                <li>Upload or transmit viruses, malware, or any malicious code</li>
                <li>Impersonate any person or entity or falsely state your affiliation</li>
                <li>Harass, abuse, or harm other users or our staff</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                Violation of this Acceptable Use Policy may result in immediate suspension or termination of your account without refund.
              </p>
            </section>

            {/* Service Availability */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Service Availability and Limitations</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">7.1 Service Availability</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We strive to provide 99.9% uptime, but we do not guarantee uninterrupted or error-free service. The Service may be unavailable due to maintenance, updates, or circumstances beyond our control. We will provide advance notice of scheduled maintenance when possible.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">7.2 Service Modifications</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice. We may add, remove, or change features without prior approval from users.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">7.3 Third-Party Dependencies</h3>
              <p className="text-slate-700 leading-relaxed">
                Our Service depends on third-party AI provider APIs (OpenAI, Anthropic, etc.). We are not responsible for changes, outages, or limitations imposed by these providers. Alert accuracy and timeliness depend on the availability and accuracy of data from these providers.
              </p>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">8.1 Our Rights</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                The Service, including all content, features, functionality, software, designs, text, graphics, logos, and trademarks, is owned by Alertaio and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">8.2 Your Rights</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You retain all rights to your data, including usage statistics, cost data, and account information. By using our Service, you grant us a limited license to access and process this data solely for the purpose of providing the Service.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">8.3 Feedback</h3>
              <p className="text-slate-700 leading-relaxed">
                Any feedback, suggestions, or ideas you provide about the Service become our property, and we may use them without compensation or attribution.
              </p>
            </section>

            {/* Disclaimers and Warranties */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Disclaimers and Warranties</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
                <p className="text-slate-700 leading-relaxed font-semibold mb-2">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
                </p>
              </div>
              <p className="text-slate-700 leading-relaxed mb-3">We disclaim all warranties, including but not limited to:</p>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Merchantability, fitness for a particular purpose, and non-infringement</li>
                <li>Accuracy, reliability, or timeliness of alerts and cost data</li>
                <li>Uninterrupted, secure, or error-free service</li>
                <li>Prevention of all budget overruns or unexpected charges</li>
              </ul>
              <p className="text-slate-700 leading-relaxed mt-4">
                Alertaio is a monitoring tool, not a spending control mechanism. We cannot prevent charges from AI providers or guarantee that alerts will prevent all overruns. You remain responsible for managing your AI API usage and costs.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
                <p className="text-slate-700 leading-relaxed font-semibold">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALERTAIO AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL.
                </p>
              </div>
              <p className="text-slate-700 leading-relaxed mb-3">
                Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid us in the 12 months preceding the claim, or $100, whichever is greater.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We are not responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mt-2">
                <li>AI API charges incurred despite receiving alerts</li>
                <li>Delayed or failed alerts due to third-party service issues</li>
                <li>Inaccurate cost data from AI providers</li>
                <li>Business losses or damages resulting from service unavailability</li>
              </ul>
            </section>

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
              <p className="text-slate-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Alertaio, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mt-3">
                <li>Your use or misuse of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Your unauthorized or fraudulent use of API keys</li>
              </ul>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Dispute Resolution and Governing Law</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">12.1 Governing Law</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to conflict of law principles.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">12.2 Informal Resolution</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Before filing a claim, you agree to contact us at <a href="mailto:support@alertaio.com" className="text-indigo-600 hover:text-indigo-700 font-semibold">support@alertaio.com</a> to attempt to resolve the dispute informally. We commit to responding within 7 business days.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">12.3 Arbitration</h3>
              <p className="text-slate-700 leading-relaxed">
                If informal resolution fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. You agree to waive your right to a jury trial or to participate in class action lawsuits.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Termination</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">13.1 By You</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                You may terminate your account at any time through your account settings or by contacting support. Termination is effective at the end of your current billing period.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">13.2 By Us</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                We may suspend or terminate your account immediately if:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-700 mb-4">
                <li>You violate these Terms or our Acceptable Use Policy</li>
                <li>Your account is inactive for more than 12 months</li>
                <li>We are required to do so by law or court order</li>
                <li>We discontinue the Service (with 30 days' notice and pro-rated refund)</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">13.3 Effect of Termination</h3>
              <p className="text-slate-700 leading-relaxed">
                Upon termination, your right to use the Service ceases immediately. We will delete your account data within 90 days unless retention is required by law. Sections of these Terms that by their nature should survive termination shall survive, including disclaimers, limitations of liability, and dispute resolution provisions.
              </p>
            </section>

            {/* General Provisions */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">14. General Provisions</h2>
              
              <h3 className="text-xl font-semibold text-slate-800 mb-3">14.1 Entire Agreement</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Alertaio regarding the Service.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">14.2 Severability</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">14.3 Waiver</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
              </p>

              <h3 className="text-xl font-semibold text-slate-800 mb-3">14.4 Assignment</h3>
              <p className="text-slate-700 leading-relaxed">
                You may not assign or transfer these Terms or your account without our prior written consent. We may assign our rights and obligations without restriction.
              </p>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">15. Contact Information</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you have questions about these Terms or need assistance, please contact us:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 mb-2"><strong>Email:</strong> <a href="mailto:support@alertaio.com" className="text-indigo-600 hover:text-indigo-700">support@alertaio.com</a></p>
                <p className="text-slate-700 mb-2"><strong>Legal:</strong> <a href="mailto:legal@alertaio.com" className="text-indigo-600 hover:text-indigo-700">legal@alertaio.com</a></p>
                <p className="text-slate-700"><strong>Website:</strong> <a href="https://alertaio.com" className="text-indigo-600 hover:text-indigo-700">alertaio.com</a></p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Acknowledgment</h3>
              <p className="text-slate-700 leading-relaxed">
                BY USING ALERTAIO, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE. IF YOU DO NOT AGREE, YOU MUST NOT ACCESS OR USE THE SERVICE.
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link to="/" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
